'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useClientConfig, type Currency } from '@shared/utils';
import { useCallback } from 'react';
import type { DetailedTicket, LotteryName, Participation } from '../../../lottery-purchases/types';
import { getLinePrizeCents, isParticipationSettled, toDrawnLines } from '../../../lottery-purchases/utils/participationResults/participationResults';
import type { YourNumbersTicket } from '../../components/YourNumbersSection/YourNumbersSection';
import type { DrawnNumbers, DrawnNumbersSource } from '../../liveDraw/getRevealedNumbers';

/** The drawing tickets endpoint pages; one player's tickets for a single drawing fit in one page. */
const DRAWING_TICKETS_PAGE_SIZE = 100;

/**
 * How often to re-ask for the prize once the balls exist.
 *
 * Numbers land at the drawing instant; scoring follows shortly after. The reveal still has to
 * play out, so polling starts as soon as the balls are known — not after the last one has
 * settled. That way a prize that arrives mid-reveal can skip the calculating board and go
 * straight to You won! The query otherwise carries a 5 minute `staleTime`, so without this a
 * player already on the page would sit on a pre-draw participation until something else
 * invalidated it.
 */
const AWAITING_EVALUATION_POLL_MS = 2_000;

function findParticipation(ticket: DetailedTicket, drawingId: string): Participation | undefined {
  return ticket.participationInfo?.find(participation => participation.drawingId === drawingId);
}

/**
 * This lottery's tickets that should appear under Your Numbers for the given drawing.
 *
 * A participation in `VOID` is left out: the slip would still show the player's numbers, and a
 * prize on it would read as a win. The ticket's own state is irrelevant — voiding a ticket only
 * marks its future participations `VOID`, so a past draw on that ticket still belongs here.
 */
function findParticipatingTickets(tickets: readonly DetailedTicket[] | null | undefined, lotteryName: LotteryName, drawingId: string): DetailedTicket[] {
  return (tickets ?? []).filter(ticket => {
    const participation = findParticipation(ticket, drawingId);
    return ticket.lotteryName === lotteryName && participation !== undefined && participation.participationState !== 'VOID';
  });
}

/**
 * A settled participation's prize in cents: its own `realWinning`, else the sum of its lines' prizes,
 * else the ticket's `totalWinning`.
 *
 * Staging has not been seen to send `realWinning`. When a Daily Million ticket is billed, the prize
 * is only on the ticket's `totalWinning`; the per-line `lineWinningInfo` fills in some time later.
 * `totalWinning` is a ticket-level figure, so it is only used as this drawing's prize when the
 * ticket is in no other drawing.
 */
function getParticipationPrize(ticket: DetailedTicket, participation: Participation, currency: Currency): number {
  if (participation.realWinning !== undefined) {
    return participation.realWinning;
  }

  const lines = participation.lineWinningInfo[ticket.lotteryName];
  if (lines?.length) {
    return lines.reduce((total, info) => total + (getLinePrizeCents(info, currency) ?? 0), 0);
  }

  return ticket.participationInfo.length === 1 ? ticket.totalWinning : 0;
}

/**
 * Polls only across the window where the answer can still change: the draw has produced numbers
 * but at least one participation has not been scored. A drawing that has not run yet is left
 * alone — those participations sit unsettled for hours.
 */
function resolveEvaluationPollMs(
  data: DetailedTicket[] | null | undefined,
  { lotteryName, drawingId, hasDrawnNumbers }: Pick<UseDrawingTicketsOptions, 'lotteryName' | 'drawingId' | 'hasDrawnNumbers'>
): number | false {
  if (drawingId === undefined || !hasDrawnNumbers) {
    return false;
  }

  const participating = findParticipatingTickets(data, lotteryName, drawingId);
  if (participating.length === 0) {
    return false;
  }

  return participating.every(ticket => isParticipationSettled(findParticipation(ticket, drawingId))) ? false : AWAITING_EVALUATION_POLL_MS;
}

function collectNumbers(values: readonly unknown[] | undefined, into: Set<number>): void {
  values?.forEach(value => {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      into.add(parsed);
    }
  });
}

/**
 * Every number the player holds across all lines of the given tickets, deduplicated.
 *
 * Main and extra numbers are kept apart rather than flattened into one set: they are drawn from
 * separate pools, so Lucky for Life's Lucky ball `3` must not light main cell `3` — or the other
 * way round. `DrawnNumbersGrid` and the "Match!" ball intersect each against its own pool.
 */
function toPlayerNumbers(tickets: readonly DetailedTicket[]): DrawnNumbers {
  const numbers = new Set<number>();
  const extraNumbers = new Set<number>();

  tickets.forEach(ticket => {
    (ticket.lineInfo[ticket.lotteryName] ?? []).forEach(line => {
      collectNumbers(line.numbers, numbers);
      collectNumbers(line.extraNumbers, extraNumbers);
    });
  });

  return { numbers: [...numbers], extraNumbers: [...extraNumbers] };
}

type DrawingTickets = {
  /** The player's tickets that took part in this drawing, shaped for `YourNumbersSection`. */
  tickets: YourNumbersTicket[];
  /**
   * Numbers across those tickets, main and extra kept separate; drives the live-draw "Match!" ball
   * and the filled balls on the grid.
   */
  playerNumbers: DrawnNumbers;
  /**
   * Summed prize across those tickets in cents. Left `undefined` while the player holds no ticket
   * for the drawing *and* while the draw is still being scored, so neither reads as a confirmed zero.
   */
  totalWinnings: number | undefined;
};

const EMPTY_DRAWING_TICKETS: DrawingTickets = {
  tickets: [],
  playerNumbers: { numbers: [], extraNumbers: [] },
  totalWinnings: undefined,
};

type DrawingTicketsContext = {
  lotteryName: LotteryName;
  drawingId: string | undefined;
  currency: Currency;
  hasDrawnNumbers: boolean;
  drawnNumbers?: DrawnNumbersSource;
};

/**
 * The drawing's tickets, shaped for Your Numbers.
 *
 * `null` is a 404: the player has nothing in this drawing, same as an empty page. A drawing that
 * is not known yet cannot be matched, so that stays empty too.
 */
function toDrawingTickets(data: DetailedTicket[] | null, { lotteryName, drawingId, currency, hasDrawnNumbers, drawnNumbers }: DrawingTicketsContext): DrawingTickets {
  if (drawingId === undefined) {
    return EMPTY_DRAWING_TICKETS;
  }

  const participating = findParticipatingTickets(data, lotteryName, drawingId);
  const tickets: YourNumbersTicket[] = participating.map(ticket => {
    const participation = findParticipation(ticket, drawingId);
    const isSettled = isParticipationSettled(participation);

    return {
      ticketId: ticket.ticketId,
      lotteryName: ticket.lotteryName,
      lineInfo: ticket.lineInfo,
      drawnLines: toDrawnLines(participation, ticket.lotteryName, currency, ticket.lineInfo[ticket.lotteryName], drawnNumbers),
      // Balls are out but this ticket has not been scored: the footer says "calculating" instead
      // of reporting the zero an unevaluated participation carries.
      isCalculating: hasDrawnNumbers && !isSettled,
      totalWinnings: isSettled ? getParticipationPrize(ticket, participation, currency) : undefined,
    };
  });
  const isSettled = participating.length > 0 && participating.every(ticket => isParticipationSettled(findParticipation(ticket, drawingId)));

  return {
    tickets,
    playerNumbers: toPlayerNumbers(participating),
    totalWinnings: isSettled ? tickets.reduce((total, ticket) => total + (ticket.totalWinnings ?? 0), 0) : undefined,
  };
}

type DrawingTicketsLoadingQuery = {
  isLoading: boolean;
  isFetching: boolean;
  isStale: boolean;
};

/**
 * Whether Your Numbers should show its skeleton.
 *
 * A disabled query (no drawing, or signed out) is `isPending` forever and `isLoading` false, so
 * pending is not a loading signal. Session start-up is the other gap: the query stays disabled
 * until authentication resolves, and a skeleton is still owed for a drawing that exists. A stale
 * refetch — a purchase invalidation — has to count too. `isLoading` from the query is only true
 * when there is no data at all, so without the stale term Your Numbers flashes the cached slip
 * and then swaps. A fresh evaluation poll is fetching but not stale, and must not take the slip down.
 */
function isDrawingTicketsLoading(query: DrawingTicketsLoadingQuery, { hasDrawing, isSessionLoading }: { hasDrawing: boolean; isSessionLoading: boolean }): boolean {
  if (!hasDrawing) {
    return false;
  }

  return isSessionLoading || query.isLoading || (query.isFetching && query.isStale);
}

type DrawingTicketsEvaluationQuery = {
  isLoading: boolean;
  data: DrawingTickets | undefined;
};

/**
 * True once the balls exist and the prize is not yet known: tickets are still loading, or at
 * least one participating ticket has not been scored. Holds the calculating board, so a join
 * during the reveal does not flash the finished slip before You won! can play.
 *
 * An empty slip is not awaiting. `totalWinnings` stays `undefined` both then and while a ticket
 * is unscored, so the ticket count is what separates them.
 */
function isAwaitingDrawingEvaluation(
  query: DrawingTicketsEvaluationQuery,
  { hasDrawing, hasDrawnNumbers, isSessionLoading }: { hasDrawing: boolean; hasDrawnNumbers: boolean; isSessionLoading: boolean }
): boolean {
  if (!hasDrawing || !hasDrawnNumbers) {
    return false;
  }

  if (isSessionLoading || query.isLoading) {
    return true;
  }

  const tickets = query.data?.tickets ?? [];
  return tickets.length > 0 && query.data?.totalWinnings === undefined;
}

type UseDrawingTicketsOptions = {
  lotteryName: LotteryName;
  drawingId: string | undefined;
  /**
   * Whether *this* drawing has produced its balls, which is what separates "waiting to be scored"
   * from a ticket sitting in a drawing that has not happened yet.
   *
   * Must describe the drawing `drawingId` names, not whatever the live endpoint is serving now:
   * once the live payload rolls to the next drawing its `numbers` are empty again, and reading the
   * count off it would stop the evaluation poll while the watched drawing is still being scored.
   */
  hasDrawnNumbers?: boolean;
  /** That drawing's balls, so a previous draw's slip lights up its extra ball as well as its main ones. */
  drawnNumbers?: DrawnNumbersSource;
};

/**
 * The player's tickets for one drawing, with their lines.
 *
 * LOTD-573 returns the ticketDetail payload for every participation in that drawing, so there is
 * no list + N-detail round trip. A signed-out visitor has no tickets, so the request is never made
 * and the results stay empty, which is what `YourNumbersSection` renders its empty state for.
 */
function useDrawingTickets({ lotteryName, drawingId, hasDrawnNumbers = false, drawnNumbers }: UseDrawingTicketsOptions) {
  const { currency } = useClientConfig();
  const { isAuthenticated } = useSession();
  const hasDrawing = drawingId !== undefined;
  const path = API_ROUTES.LOTTERIES.PURCHASED_DRAWING_TICKETS.replace(':drawingId', drawingId ?? '');
  const select = useCallback(
    (data: DetailedTicket[] | null) => toDrawingTickets(data, { lotteryName, drawingId, currency, hasDrawnNumbers, drawnNumbers }),
    [currency, drawingId, drawnNumbers, hasDrawnNumbers, lotteryName]
  );

  return useApiQuery<DetailedTicket[], DrawingTickets>(path, {
    credentials: true,
    returnNullOn404: true,
    params: { pageSize: DRAWING_TICKETS_PAGE_SIZE, showResult: true },
    queryOptions: {
      enabled: hasDrawing && isAuthenticated,
      // `query.state.data` is the cached payload, before `select`.
      refetchInterval: query => resolveEvaluationPollMs(query.state.data, { lotteryName, drawingId, hasDrawnNumbers }),
      select,
    },
  });
}

export { EMPTY_DRAWING_TICKETS, isAwaitingDrawingEvaluation, isDrawingTicketsLoading, resolveEvaluationPollMs, toDrawingTickets, useDrawingTickets };
export type { DrawingTickets, UseDrawingTicketsOptions };
